'use client';

import { useEffect, useRef } from 'react';

interface Experience {
  id: string;
  company: string;
  role: string;
  period: string;
  location: string;
  type: string;
  accentColor: string;
  highlights: string[];
  stack: string[];
}

const EXPERIENCES: Experience[] = [
  {
    id: 'EXP-002',
    company: 'Philips India Ltd',
    role: 'Apprentice Trainee (Software Developer Level 1)',
    period: '2025.07 — Present',
    location: 'Pune, Maharashtra',
    type: 'FULL-TIME',
    accentColor: 'var(--cyber-green)',
    highlights: [
      'Engineered end-to-end SWO Data Analytics system processing 10,000+ service orders with AI classification and Power BI dashboards, reducing analysis time by 70%.',
      'Supported NMRS migration from Windows 7 to Windows 11, handling compatibility testing and system validation.',
      'Building to collimator calibration software development for medical equipment.',
    ],
    stack: ['Python', 'Flask', 'PowerBI', 'MySQL', 'AI', 'PHP', 'Apache'],
  },
  {
    id: 'EXP-001',
    company: 'FREELANCE_OPERATOR',
    role: 'FULL-STACK DEVELOPER',
    period: '2025.01 — 2025.06',
    location: 'REMOTE',
    type: 'PART-TIME',
    accentColor: 'var(--cyber-yellow)',
    highlights: [
      'Designed and Developed 5+ websites for different clients',
      'Clients from different domains like healthcare, education, and e-commerce',
    ],
    stack: ['React', 'Typescript', 'Node.js', 'MySQL','MongoDB'],
  },
];

export default function ExperienceSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const itemRefs   = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            entry.target.classList.remove('hidden-initial');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    itemRefs.current.forEach((el) => {
      if (el) {
        el.classList.add('hidden-initial');
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="relative py-24 px-6 md:px-12 overflow-hidden"
      style={{ background: 'var(--cyber-dark)' }}
      aria-label="Experience section"
    >
      {/* Diagonal accent */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(255,0,110,0.03) 0%, transparent 50%)',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <p className="font-mono text-xs mb-3" style={{ color: 'var(--cyber-green)', letterSpacing: '0.3em' }}>
            // MODULE:03 
          </p>
          <h2 className="font-mono font-black text-4xl md:text-6xl" style={{ color: '#FFFFFF', letterSpacing: '-0.02em' }}>
            OPS<span className="text-glow-green">_HISTORY</span>
          </h2>
          <p className="font-mono text-sm mt-4" style={{ color: 'rgba(200,216,232,0.5)' }}>
            FIELD_EXPERIENCE: {EXPERIENCES.length} DEPLOYMENTS // 2019 → PRESENT
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div
            className="absolute left-4 md:left-8 top-0 bottom-0 exp-line"
            style={{ width: '2px' }}
            aria-hidden="true"
          />

          <div className="space-y-8 pl-16 md:pl-24">
            {EXPERIENCES.map((exp, i) => (
              <div
                key={exp.id}
                ref={(el) => { if (el) itemRefs.current[i] = el; }}
                className="reveal-item relative group"
              >
                {/* Timeline node */}
                <div
                  className="absolute -left-12 md:-left-16 top-6 w-4 h-4 flex items-center justify-center"
                  style={{ transform: 'translateX(-50%)' }}
                  aria-hidden="true"
                >
                  <div
                    className="w-4 h-4 rotate-45 transition-all duration-300 group-hover:scale-150"
                    style={{
                      background: exp.accentColor,
                      boxShadow: `0 0 12px ${exp.accentColor}80`,
                    }}
                  />
                </div>

                {/* Experience card */}
                <div
                  className="cyber-panel p-6 md:p-8 transition-all duration-400 group-hover:border-opacity-60"
                  style={{
                    border: `1px solid ${exp.accentColor}20`,
                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${exp.accentColor}50`;
                    (e.currentTarget as HTMLElement).style.boxShadow = `0 0 20px ${exp.accentColor}10`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor = `${exp.accentColor}20`;
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                >
                  {/* Card header */}
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <span
                          className="font-mono text-[9px] px-2 py-0.5"
                          style={{
                            background: `${exp.accentColor}12`,
                            border: `1px solid ${exp.accentColor}30`,
                            color: exp.accentColor,
                            letterSpacing: '0.15em',
                          }}
                        >
                          {exp.id}
                        </span>
                        <span
                          className="font-mono text-[9px] px-2 py-0.5"
                          style={{
                            background: 'rgba(200,216,232,0.05)',
                            border: '1px solid rgba(200,216,232,0.1)',
                            color: 'rgba(200,216,232,0.5)',
                            letterSpacing: '0.1em',
                          }}
                        >
                          {exp.type}
                        </span>
                      </div>
                      <h3 className="font-mono font-black text-lg md:text-2xl mb-1" style={{ color: '#FFFFFF', letterSpacing: '-0.01em' }}>
                        {exp.role}
                      </h3>
                      <p className="font-mono font-bold text-sm" style={{ color: exp.accentColor }}>
                        {exp.company}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono text-xs font-bold mb-1" style={{ color: 'rgba(200,216,232,0.7)' }}>
                        {exp.period}
                      </div>
                      <div className="font-mono text-[10px]" style={{ color: 'rgba(200,216,232,0.4)', letterSpacing: '0.1em' }}>
                        {exp.location}
                      </div>
                    </div>
                  </div>

                  {/* Highlights */}
                  <div className="space-y-2 mb-5">
                    {exp.highlights.map((h, hi) => (
                      <div key={hi} className="flex items-start gap-3">
                        <span className="font-mono text-xs mt-0.5 shrink-0" style={{ color: exp.accentColor }}>▸</span>
                        <p className="font-mono text-xs leading-relaxed" style={{ color: 'rgba(200,216,232,0.65)' }}>
                          {h}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Stack */}
                  <div className="flex flex-wrap gap-2 pt-4" style={{ borderTop: `1px solid ${exp.accentColor}15` }}>
                    {exp.stack.map((s) => (
                      <span
                        key={s}
                        className="font-mono text-[9px] px-2 py-0.5"
                        style={{
                          background: `${exp.accentColor}08`,
                          border: `1px solid ${exp.accentColor}20`,
                          color: 'rgba(200,216,232,0.5)',
                          letterSpacing: '0.1em',
                        }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}